class ProductAddToCartSticky extends HTMLElement {
  constructor() {
    super();
    /** @type {string} */
    this.sectionId = this.dataset.section || "";
    /** @type {HTMLElement | null} */
    this.header = this.querySelector(".product-add-to-cart-sticky--header");
    /** @type {HTMLButtonElement | null} */
    this.stickyButton = this.querySelector("#AddToCartSticky");
    /** @type {HTMLElement | null} */
    this.buttonText = this.querySelector(
      ".product-add-to-cart-sticky--button-text"
    );
    /** @type {NodeListOf<HTMLSelectElement>} */
    this.optionSelects = this.querySelectorAll(
      ".product-add-to-cart-sticky--select"
    );
    /** @type {Array} */
    this.variants = [];

    // Bind methods
    this.onScroll = this.onScroll.bind(this);
    this.toggleContent = this.toggleContent.bind(this);
    this.updateVisibility = this.updateVisibility.bind(this);
    this.handleStickyClick = this.handleStickyClick.bind(this);
    this.onOptionChange = this.onOptionChange.bind(this);

    /** @type {HTMLButtonElement | null} */
    this.mainAddToCartButton = null;
    /** @type {HTMLInputElement | null} */
    this.mainVariantInput = null;
    /** @type {Element | null} */
    this.observerTarget = null;
  }

  connectedCallback() {
    this.loadVariants();
    this.init();
    window.addEventListener("scroll", this.onScroll, { passive: true });

    if (this.header) {
      this.header.addEventListener("click", this.toggleContent);
    }

    if (this.stickyButton) {
      this.stickyButton.addEventListener("click", this.handleStickyClick);
    }

    // Listen for option changes
    this.optionSelects.forEach((select) => {
      select.addEventListener("change", this.onOptionChange);
    });

    // Initial check
    this.updateVisibility();
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScroll);
    if (this.header) {
      this.header.removeEventListener("click", this.toggleContent);
    }
    if (this.stickyButton) {
      this.stickyButton.removeEventListener("click", this.handleStickyClick);
    }
    this.optionSelects.forEach((select) => {
      select.removeEventListener("change", this.onOptionChange);
    });
  }

  loadVariants() {
    const variantsScript = this.querySelector(
      `#sticky-variants-${this.sectionId}`
    );
    if (variantsScript) {
      try {
        this.variants = JSON.parse(variantsScript.textContent || "[]");
      } catch (e) {
        console.error("Failed to parse variants", e);
        this.variants = [];
      }
    }
  }

  init() {
    const sectionContainer =
      document.querySelector(`.section[data-section-id="${this.sectionId}"]`) ||
      document.querySelector(`[data-section-id="${this.sectionId}"]`) ||
      document.body;

    // Find the main form
    const mainForm = sectionContainer.querySelector(
      'form[action*="/cart/add"]'
    );
    if (mainForm) {
      this.mainAddToCartButton = mainForm.querySelector('button[name="add"]');
      this.mainVariantInput = mainForm.querySelector('input[name="id"]');
    }

    // Fallback for button
    if (!this.mainAddToCartButton) {
      this.mainAddToCartButton = sectionContainer.querySelector(
        ".add-to-cart-button"
      );
    }

    this.observerTarget =
      this.mainAddToCartButton || mainForm || sectionContainer;
  }

  onScroll() {
    requestAnimationFrame(this.updateVisibility);
  }

  updateVisibility() {
    if (!this.observerTarget) {
      this.init();
      if (!this.observerTarget) return;
    }

    const rect = this.observerTarget.getBoundingClientRect();
    const isOutOfView = rect.bottom < 0;

    if (isOutOfView) {
      this.classList.add("is-visible");
    } else {
      this.classList.remove("is-visible");
      this.classList.remove("is-expanded");
    }
  }

  toggleContent() {
    this.classList.toggle("is-expanded");
  }

  /**
   * Find the matching variant based on selected options
   * @returns {Object|null}
   */
  getSelectedVariant() {
    const selectedOptions = [];
    this.optionSelects.forEach((select) => {
      selectedOptions.push(select.value);
    });

    // Find variant that matches all selected options
    return (
      this.variants.find((variant) => {
        return selectedOptions.every((option, index) => {
          return variant.options[index] === option;
        });
      }) || null
    );
  }

  onOptionChange() {
    const variant = this.getSelectedVariant();

    if (variant && this.stickyButton && this.buttonText) {
      // @ts-ignore
      const translations = window.Theme?.translations;

      if (variant.available) {
        this.stickyButton.disabled = false;
        this.buttonText.textContent =
          translations?.add_to_cart || "Add to Cart";
      } else {
        this.stickyButton.disabled = true;
        this.buttonText.textContent = translations?.sold_out || "Sold Out";
      }

      // Update the main form's variant input
      if (this.mainVariantInput) {
        this.mainVariantInput.value = variant.id.toString();
      }

      // Also try to sync with main variant picker by clicking corresponding option
      this.syncMainVariantPicker(variant);
    }
  }

  /**
   * Sync the main page variant picker with sticky selection
   * @param {Object} variant
   */
  syncMainVariantPicker(variant) {
    const sectionContainer =
      document.querySelector(`.section[data-section-id="${this.sectionId}"]`) ||
      document.querySelector(`[data-section-id="${this.sectionId}"]`);

    if (!sectionContainer) return;

    // Try to find and update the main variant picker selects
    variant.options.forEach((optionValue, index) => {
      // Look for select elements that are NOT inside this sticky bar
      const mainSelect = sectionContainer.querySelector(
        `select[data-option-index="${index}"]:not(.product-add-to-cart-sticky--select)`
      );

      if (mainSelect instanceof HTMLSelectElement) {
        mainSelect.value = optionValue;
        mainSelect.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  /**
   * @param {MouseEvent} event
   */
  handleStickyClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const variant = this.getSelectedVariant();

    if (!variant) {
      console.warn("No variant selected");
      return;
    }

    if (!variant.available) {
      return;
    }

    // Update main form variant ID before clicking
    this.init();
    if (this.mainVariantInput) {
      this.mainVariantInput.value = variant.id.toString();
    }

    if (this.mainAddToCartButton && this.stickyButton) {
      // Check if main button is disabled
      if (this.mainAddToCartButton.disabled) {
        return;
      }

      // Show loading state
      this.stickyButton.classList.add("is-loading");

      // Click the main add to cart button
      this.mainAddToCartButton.click();

      // Reset loading state after timeout
      setTimeout(() => {
        if (this.stickyButton) {
          this.stickyButton.classList.remove("is-loading");
        }
      }, 2000);
    }
  }
}

if (!customElements.get("product-add-to-cart-sticky")) {
  customElements.define("product-add-to-cart-sticky", ProductAddToCartSticky);
}
